import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { getUserList, updatedUsers, updatedUserCount } from '../../actions/adminUserDash';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

// --- Sub-components ---

const SearchBar = ({
  queryInput,
  parsedQuery,
  onInputChange,
  onSubmit,
  onReset,
}: {
  queryInput: string;
  parsedQuery: string;
  onInputChange: (e: any) => void;
  onSubmit: (e: any) => void;
  onReset: () => void;
}) => (
  <div className="admin-card-body" style={{ borderBottom: '1px solid #e1e3e5' }}>
    <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
      <input
        className="form-control form-control-sm"
        placeholder="Search users…"
        value={queryInput}
        onChange={onInputChange}
        style={{ maxWidth: 300, fontSize: '0.82rem' }}
      />
    </form>
    {parsedQuery && (
      <div style={{ marginTop: 8 }}>
        <span
          className="status-badge cancelled"
          style={{ cursor: 'pointer', fontSize: '0.75rem' }}
          onClick={onReset}
        >
          {parsedQuery} &times;
        </span>
      </div>
    )}
  </div>
);

const ucFirst = (str: string) =>
  str.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const UsersTable = ({ users, onRowClick }: { users: any[]; onRowClick: (u: any) => void }) => (
  <div className="table-responsive">
    <table className="admin-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Member Since</th>
          <th>Orders</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {users.map((user: any) => (
          <tr key={user._id} onClick={() => onRowClick(user)}>
            <td style={{ fontWeight: 600 }}>
              {ucFirst(user.firstName)} {ucFirst(user.lastName)}
            </td>
            <td className="order-email">{user.email}</td>
            <td>{user.phoneNumber}</td>
            <td className="order-time">{moment(user.dateCreated).format('MMM Do YY')}</td>
            <td>{user.orders.length}</td>
            <td>
              <span className={`status-badge ${user.isActive ? 'confirmed' : 'cancelled'}`}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pagination = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
}: {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
}) => (
  <div className="admin-card-body" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
    <button
      className="admin-dropdown-trigger"
      onClick={onPrevious}
      disabled={currentPage <= 1}
      style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
    >
      Previous
    </button>
    <button
      className="admin-dropdown-trigger"
      onClick={onNext}
      disabled={currentPage >= totalPages}
      style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
    >
      Next
    </button>
  </div>
);

// --- Main component ---

const UserDashboard = ({ users, getUserList }: any) => {
  const history = useHistory();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState(location.search);
  const [queryInput, setQueryInput] = useState('');

  const searchQueryInitialized = useRef(false);
  const locationSearchInitialized = useRef(false);

  const updateSearchQuery = (parsed: any) => setSearchQuery('?' + queryString.stringify(parsed));

  useEffect(() => {
    const parsed = queryString.parse(location.search) as any;
    let page: any = 1, filterVal: any, query: any;

    if (parsed.page) page = parsed.page;
    if (parsed.filter) filterVal = parsed.filter;
    if (parsed.query) { query = parsed.query; setQueryInput(''); }

    getUserList(page, filterVal, query);
  }, []);

  useEffect(() => {
    if (!searchQueryInitialized.current) { searchQueryInitialized.current = true; return; }
    history.push(`/adminpanel/users${searchQuery}`);
  }, [searchQuery]);

  useEffect(() => {
    if (!locationSearchInitialized.current) { locationSearchInitialized.current = true; return; }
    const parsed = queryString.parse(location.search) as any;
    let page: any, filterVal: any, query: any;

    if (parsed.page) page = parsed.page;
    if (parsed.filter) filterVal = parsed.filter;
    if (parsed.query) query = parsed.query;

    updateSearchQuery(parsed);
    window.scrollTo(0, 0);
    getUserList(page, filterVal, query);
  }, [location.search]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const parsed = queryString.parse(location.search) as any;
    parsed.query = queryInput;
    parsed.page = undefined;
    setQueryInput('');
    updateSearchQuery(parsed);
  };

  const handleReset = () => setSearchQuery('');

  const previousPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parseInt(parsed.page as string) - 1;
    updateSearchQuery(parsed);
  };

  const nextPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parsed.page ? parseInt(parsed.page as string) + 1 : 2;
    updateSearchQuery(parsed);
  };

  const handleUserClick = (user: any) => {
    history.push({
      pathname: `/adminpanel/users/${user.firstName}${user.lastName}/${user._id}`,
      state: { user },
    });
  };

  const parsed = queryString.parse(location.search) as any;
  const PAGE_SIZE = 24;
  const totalPages = Math.ceil(users.userCount / PAGE_SIZE);
  const currentPage = parsed.page
    ? parsed.page > totalPages ? totalPages : parsed.page <= 1 ? 1 : parseInt(parsed.page)
    : 1;
  const end = Math.min(PAGE_SIZE * currentPage, users.userCount);
  const startItem = currentPage <= 1 ? 1 : (currentPage - 1) * PAGE_SIZE + 1;

  return (
    <div>
      <Helmet>
        <title>Users - Frozberries Victoria</title>
        <meta name="description" content="Users - Frozberries Victoria" />
      </Helmet>

      {users.loading ? (
        <div className="admin-empty-state">
          <i className="fas fa-spinner fa-spin" />
          <p>Loading users…</p>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-card-header">
            <p className="admin-card-title">Users</p>
            {users.userCount > 0 && (
              <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>
                {startItem}–{end} of {users.userCount}
              </span>
            )}
          </div>

          <SearchBar
            queryInput={queryInput}
            parsedQuery={parsed.query}
            onInputChange={(e) => setQueryInput(e.target.value)}
            onSubmit={handleSubmit}
            onReset={handleReset}
          />

          {users.userCount === 0 ? (
            <div className="admin-empty-state">
              <i className="fas fa-users" />
              <p>No users found.</p>
            </div>
          ) : (
            <>
              <div className="admin-card-body-flush">
                <UsersTable users={users.users} onRowClick={handleUserClick} />
              </div>

              {totalPages > 1 && users.users.length !== 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevious={previousPage}
                  onNext={nextPage}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  users: state.users,
});

export default connect(mapStateToProps, {
  getUserList,
  updatedUsers,
  updatedUserCount,
})(UserDashboard);
